import { useEffect, useRef, useState } from "react";

import {
	getPaymentElementLoadBlockedCode,
	PAYMENT_ELEMENT_LOAD_TIMEOUT_MS,
	type PaymentElementLoadError,
	type PaymentElementLoadStatus,
	reducePaymentElementLoadState,
	shouldRunPaymentElementWatchdog,
} from "~/lib/stripe/paymentElementLoadState";

type BlockedCode = ReturnType<typeof getPaymentElementLoadBlockedCode>;

/**
 * Tracks PaymentElement readiness with onReady / onLoadError and a watchdog
 * timeout so checkout never sits on "Loading card form…" forever.
 *
 * @param options.enabled - When false, the watchdog is paused (form not shown)
 * @param options.isStripeReady - When false, the watchdog waits for Stripe.js
 * @param options.timeoutMs - Watchdog timeout (default 15s)
 * @param options.onBlocked - Fired once when timeout or load_error wins, with Stripe's error for load_error
 */
const usePaymentElementLoadState = (options?: {
	enabled?: boolean;
	isStripeReady?: boolean;
	timeoutMs?: number;
	onBlocked?: (code: BlockedCode, error?: PaymentElementLoadError) => void;
}) => {
	const enabled = options?.enabled ?? true;
	const isStripeReady = options?.isStripeReady ?? true;
	const timeoutMs = options?.timeoutMs ?? PAYMENT_ELEMENT_LOAD_TIMEOUT_MS;
	const onBlockedRef = useRef(options?.onBlocked);
	onBlockedRef.current = options?.onBlocked;

	const [status, setStatus] = useState<PaymentElementLoadStatus>("loading");
	const [remountKey, setRemountKey] = useState(0);

	const isWatchdogRunning = shouldRunPaymentElementWatchdog({
		isCardFormShown: enabled,
		isStripeReady,
	});

	useEffect(() => {
		if (!enabled) {
			return;
		}

		setStatus("loading");
		setRemountKey((key) => key + 1);
	}, [enabled]);

	useEffect(() => {
		if (!isWatchdogRunning || status !== "loading") {
			return;
		}

		const timer = window.setTimeout(() => {
			setStatus((current) => {
				const next = reducePaymentElementLoadState(current, "timeout");
				if (next === "error" && current === "loading") {
					onBlockedRef.current?.(getPaymentElementLoadBlockedCode("timeout"));
				}
				return next;
			});
		}, timeoutMs);

		return () => {
			window.clearTimeout(timer);
		};
	}, [isWatchdogRunning, remountKey, status, timeoutMs]);

	const onReady = () => {
		setStatus((current) => reducePaymentElementLoadState(current, "ready"));
	};

	const onLoadError = (event?: { error?: PaymentElementLoadError }) => {
		setStatus((current) => {
			const next = reducePaymentElementLoadState(current, "load_error");
			if (next === "error" && current === "loading") {
				onBlockedRef.current?.(
					getPaymentElementLoadBlockedCode("load_error"),
					event?.error,
				);
			}
			return next;
		});
	};

	const retry = () => {
		setStatus((current) => reducePaymentElementLoadState(current, "retry"));
		setRemountKey((key) => key + 1);
	};

	return {
		status,
		remountKey,
		isReady: status === "ready",
		isError: status === "error",
		isLoading: status === "loading",
		onReady,
		onLoadError,
		retry,
	};
};

export default usePaymentElementLoadState;
